import { PenIcon } from "@xola/icons";
import { flash, FormGroup, ImageUpload, Label, Spinner } from "@xola/ui-kit";
import { FormField, InputField, TextareaField } from "common";
import { useFormikContext } from "formik";
import React, { useCallback, useMemo } from "react";
import { CollectionNodeSequences, ProductObject, SelectionState, SelectionStateWithSequence } from "types";
import { SingleParentCollectionSelector } from "./ParentCollectionSelector";
import { ProductOrCatalogSelectorModal } from "@/components/Common/ProductOrCatalogSelectorModal";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { SelectedProductOrCatalogList } from "@/components/Common/SelectedProductOrCatalogList";
import { useProducts } from "@/hooks/resources/useProduct";
import { useModal } from "@/hooks/useVisibility";

interface EditCollectionFieldsProps {
    imageUrl: string;
    onImageChange: (file: File) => void;
    onImageDelete: () => void;
    isLoadingItems?: boolean;
    isFetchingNextPageCollectionNode?: boolean;
    currentCollectionId?: ID;
}

interface EditCollectionFormState {
    path: string;
    title: string;
    description: string;
    items: SelectionStateWithSequence;
}

export const EditCollectionFields = ({
    imageUrl,
    onImageChange,
    onImageDelete,
    isLoadingItems = false,
    isFetchingNextPageCollectionNode = false,
    currentCollectionId,
}: EditCollectionFieldsProps) => {
    const { values, setFieldValue } = useFormikContext<EditCollectionFormState>();
    const itemsSelectorModal = useModal();
    const { products } = useProducts();

    const selectedItemsCount = useMemo(() => {
        if (values.items.all) {
            return "All";
        }
        return values.items.selected.length;
    }, [values.items]);

    // Determine default enabled product types based on selected items, used to show in default selected product types in ProductOrCatalogSelectorModal
    const defaultEnabledProductObjects = useMemo(() => {
        // If no items are selected, default to EXPERIENCE only
        if (values.items.selected.length === 0) {
            return [ProductObject.EXPERIENCE];
        }

        // Get unique product types from selected items
        const selectedProductIds = new Set(values.items.selected.map((item) => item.id));
        const selectedProducts = products.filter((product) => selectedProductIds.has(product.id));
        const uniqueTypes = [...new Set(selectedProducts.map((product) => product.object))];

        // Return the unique types found in selected items
        return uniqueTypes;
    }, [values.items.selected, products]);

    const handleItemsSelectionApply = (selectionState: SelectionState) => {
        // Convert SelectionState (with IDs) to SelectionStateWithSequence
        let nextSequence =
            CollectionNodeSequences.COLLECTION_NODE_SEQUENCE_START +
            CollectionNodeSequences.COLLECTION_NODE_SEQUENCE_INCREMENT;

        // Get the highest sequence from the last item (array is already sorted by sequence)
        if (values.items.selected.length > 0) {
            const lastItem = values.items.selected[values.items.selected.length - 1];
            nextSequence = lastItem.sequence + CollectionNodeSequences.COLLECTION_NODE_SEQUENCE_INCREMENT;
        }

        // Create map of existing items for quick lookup
        const existingItemsMap = new Map(values.items.selected.map((item) => [item.id, item]));

        // Convert selected IDs to items with sequences
        const itemsWithSequence = selectionState.selected.map((productId) => {
            const existingItem = existingItemsMap.get(productId);
            if (existingItem) {
                return existingItem;
            }
            const newItem = { id: productId, sequence: nextSequence };
            nextSequence += CollectionNodeSequences.COLLECTION_NODE_SEQUENCE_INCREMENT;
            return newItem;
        });

        setFieldValue("items", {
            all: selectionState.all,
            selected: itemsWithSequence,
        });
    };

    const handleParentCollectionChange = (path: string) => {
        setFieldValue("path", path);
    };

    const handleItemsChange = useCallback((newValue: SelectionStateWithSequence) => {
        setFieldValue("items", newValue);
        // setFieldValue is stable in Formik, safe to omit from dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="space-y-6">
            <FormField name="path" label="Parent Collection" tooltip="Select a parent collection for this collection.">
                <SingleParentCollectionSelector
                    value={values.path}
                    currentCollectionId={currentCollectionId}
                    onChange={handleParentCollectionChange}
                />
            </FormField>

            <FormField name="title" label="Title">
                <InputField isRequired />
            </FormField>

            <FormField name="description" label="Description">
                <TextareaField rows={4} />
            </FormField>

            <FormGroup>
                <Label>Image</Label>
                <fieldset>
                    <ImageUpload
                        src={imageUrl}
                        hasDelete={false}
                        isLoading={false}
                        requirements={
                            <div className="text-gray-500 mt-2 text-sm">
                                Check that the image is in PNG, JPG or WebP format and does not exceed 5 MB
                            </div>
                        }
                        onChange={onImageChange}
                        onDelete={onImageDelete}
                        onError={(error: string) => flash.danger(error)}
                    />
                </fieldset>
            </FormGroup>

            <FormGroup>
                <div className="mb-2 flex items-end justify-between">
                    <div className="flex items-baseline gap-2">
                        <Label>Items</Label>
                        {selectedItemsCount !== 0 && (
                            <span className="text-sm text-gray-dark">{selectedItemsCount}</span>
                        )}
                        {isLoadingItems || isFetchingNextPageCollectionNode ? <Spinner size="xs" className="" /> : null}
                    </div>
                    <SecondaryButton size="small" onClick={itemsSelectorModal.show}>
                        <PenIcon data-testid="collection-items-edit-button" />
                    </SecondaryButton>
                </div>

                {isLoadingItems ? (
                    <div className="border border-gray-lighter p-6 text-center">
                        <Spinner size="medium" />
                    </div>
                ) : selectedItemsCount === 0 ? (
                    <div className="border border-gray-lighter p-6 text-center">
                        <p className="mb-4 text-sm text-gray-darker">
                            Choose the items to include in this collection. Items can be included in more than one
                            Collection.
                        </p>
                        <SecondaryButton size="small" onClick={itemsSelectorModal.show}>
                            Select Items
                        </SecondaryButton>
                    </div>
                ) : (
                    <div className="max-h-80 overflow-y-auto">
                        <SelectedProductOrCatalogList
                            showDragAndDropProductList
                            selectedProducts={values.items}
                            onChange={handleItemsChange}
                        />
                    </div>
                )}
            </FormGroup>

            <ProductOrCatalogSelectorModal
                hideDraft
                enableSelectAll
                allowEmptySelection
                description="Choose the items to include in this collection. Items can be included in more than one Collection."
                isOpen={itemsSelectorModal.isVisible}
                defaultEnabledProductObjects={defaultEnabledProductObjects}
                value={{
                    all: values.items.all,
                    selected: values.items.selected.map((item) => item.id),
                }}
                onClose={itemsSelectorModal.hide}
                onConfirm={handleItemsSelectionApply}
            />
        </div>
    );
};
